/**
 * Single source of truth for "is this a working day?" across Kronos.
 *
 * The four entry modes and the report all reason about days differently
 * (calendar columns, week-relative indices, raw `startTime`), so the rule
 * lives here and nowhere else — otherwise it drifts between views.
 *
 * ponytail: French public holidays only, computed in code. No Firestore,
 * no admin screen, no dependency. Add a Firestore override collection here
 * the day Alsace-Moselle or the Chinese calendar (with its 调休 worked
 * Saturdays) actually needs covering — every caller goes through
 * `nonWorkingDay`, so only this file changes.
 */

export type NonWorkingDay =
  | { kind: 'weekend' }
  /** `name` is the French holiday name, already human-readable. */
  | { kind: 'holiday'; name: string };

/**
 * Easter Sunday for a Gregorian year (Meeus/Jones/Butcher algorithm).
 * Returns a local-midnight Date.
 */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Local-midnight ms for a date, the key used everywhere below. */
export function dayKey(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function shift(from: Date, days: number): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/** The 11 French public holidays of a year, keyed by local-midnight ms. */
function buildHolidays(year: number): Map<number, string> {
  const easter = easterSunday(year);
  const entries: Array<[Date, string]> = [
    [new Date(year, 0, 1), 'Jour de l’An'],
    [shift(easter, 1), 'Lundi de Pâques'],
    [new Date(year, 4, 1), 'Fête du Travail'],
    [new Date(year, 4, 8), 'Victoire 1945'],
    [shift(easter, 39), 'Ascension'],
    [shift(easter, 50), 'Lundi de Pentecôte'],
    [new Date(year, 6, 14), 'Fête nationale'],
    [new Date(year, 7, 15), 'Assomption'],
    [new Date(year, 10, 1), 'Toussaint'],
    [new Date(year, 10, 11), 'Armistice 1918'],
    [new Date(year, 11, 25), 'Noël'],
  ];
  return new Map(entries.map(([d, name]) => [dayKey(d), name]));
}

const holidayCache = new Map<number, Map<number, string>>();

function holidaysOf(year: number): Map<number, string> {
  let cached = holidayCache.get(year);
  if (!cached) {
    cached = buildHolidays(year);
    holidayCache.set(year, cached);
  }
  return cached;
}

/**
 * `null` when the day is an ordinary working day, otherwise why it isn't.
 * The only function callers should need.
 */
export function nonWorkingDay(date: Date): NonWorkingDay | null {
  const name = holidaysOf(date.getFullYear()).get(dayKey(date));
  if (name) return { kind: 'holiday', name };
  const weekday = date.getDay();
  if (weekday === 0 || weekday === 6) return { kind: 'weekend' };
  return null;
}

/** Convenience predicate for callers that only need the boolean. */
export function isNonWorkingDay(date: Date): boolean {
  return nonWorkingDay(date) !== null;
}
